<template>
  <div class="h-full">
    <CategoryList
      v-if="!query || filteredCategories.length > 0"
      :selected="type === 'single' ? selected[0] : selected"
      :type="type"
      :categories="filteredCategories"
      @update:selected="handleChange"
    />
    <NoCategories v-else :query="query" />
  </div>
</template>

<script setup>
import { computed, ref, watchEffect } from "vue";
import { isEqual, sortBy } from "lodash";
import { CategoryList, NoCategories } from "@/Components/Categories";
import { useCategories } from "@/Composables/useCategories";

const emit = defineEmits(["apply", "isLoaded"]);

const props = defineProps({
  initialState: { type: Array, default: () => [] },
  type: {
    type: String,
    validator: (val) => ["single", "multiple"].includes(val),
    default: "multiple",
  },
  query: { type: String, default: "" },
  siteIds: { type: Array, default: () => [] },
});

const selected = ref([...props.initialState]);

const { isLoading, data } = useCategories(props.siteIds);

const categories = computed(() => {
  if (isLoading.value) {
    return [];
  }
  if (props.siteIds.length === 1) {
    return data.value[props.siteIds[0]];
  }
  return data.value;
});

watchEffect(() => {
  if (isLoading.value) {
    return;
  }
  if (!isLoading.value) {
    emit("isLoaded");
  }
  if (props.siteIds.length === 1) {
    selected.value = categories.value
      .filter((category) => props.initialState.includes(category.id))
      .map((category) => category.id);
  } else {
    selected.value = categories.value.reduce((acc, cur) => {
      if (cur.ids.some((id) => props.initialState.includes(id))) {
        acc.push(cur.id);
      }
      return acc;
    }, []);
  }
});

const internalState = computed(() => {
  if (props.siteIds.length === 1) {
    return selected.value;
  }
  return selected.value
    .map((id) => categories.value.find((category) => category.id === id).ids)
    .flat();
});

const parsedQuery = computed(() => props.query.trim().toLowerCase());

const filteredCategories = computed(() => {
  if (!parsedQuery.value) {
    return categories.value;
  }
  return categories.value.filter((category) => {
    return category.name.trim().toLowerCase().includes(parsedQuery.value);
  });
});

const disableApply = computed(() => {
  return isEqual(sortBy(props.initialState), sortBy(internalState.value));
});

function handleChange(newSelected) {
  if (newSelected && props.type === "single") {
    selected.value[0] = newSelected;
  } else {
    selected.value = newSelected || [];
  }
}

function handleApply() {
  emit("apply", internalState.value);
}

defineExpose({
  showBack: false,
  showApply: true,
  showSearch: true,
  disableApply,
  handleBack: () => {},
  handleApply,
});
</script>
